import type { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Info } from "lucide-react";
import { SectionCard } from "@/components/admin/cards";
import { cn } from "@/lib/utils";

/** Evo theme colours for charts (CSS variables, so they follow light / dark mode). */
export const CHART = {
  primary: "var(--primary)",
  success: "var(--success-500)",
  warning: "var(--warning-500)",
  danger: "var(--destructive)",
  info: "var(--info-500)",
  muted: "var(--muted-foreground)",
  grid: "var(--border)",
  text: "var(--foreground)",
  surface: "var(--card)",
};

export const PALETTE = [CHART.primary, CHART.success, CHART.warning, CHART.info, CHART.danger, "var(--chart-2)", "var(--chart-4)"];

export const axisTick = { fontSize: 11, fill: CHART.muted };

export const formatNumber = (num) => {
  if (num == null || isNaN(num)) return "0";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

export const ChartCard = ({ icon, title, description, actions, className, children }: {
  icon: ReactNode; title: ReactNode; description: string; actions?: ReactNode; className?: string; children: ReactNode;
}) => (
  <SectionCard icon={icon} title={title} description={description} actions={actions} className={cn("analytics-chart-section", className)}>
    {children}
  </SectionCard>
);

export const ChartEmpty = ({ icon, text }: { icon: ReactNode; text: string }) => (
  <div className="analytics-empty flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">
    <span className="flex size-10 items-center justify-center rounded-full bg-secondary text-primary [&_svg]:size-5" aria-hidden="true">{icon}</span>
    <p className="analytics-empty-text">{text}</p>
  </div>
);

export const TooltipBox = ({ title, children }: { title: ReactNode; children: ReactNode }) => (
  <div data-evo-island className="rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
    <p className="mb-1 font-semibold">{title}</p>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

export const Dot = ({ color }: { color: string }) => (
  <svg viewBox="0 0 10 10" className="size-2.5 shrink-0" aria-hidden="true"><circle cx="5" cy="5" r="5" fill={color} /></svg>
);

export const legendContent = ({ payload }: any) => (
  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-2 text-xs text-muted-foreground">
    {(payload || []).map((p) => (
      <span key={p.value} className="inline-flex items-center gap-1.5"><Dot color={p.color || p.payload?.fill} />{p.value}</span>
    ))}
  </div>
);

export const getTrend = (current, prev) => {
  if (!prev || prev === 0) return { direction: "neutral", pct: 0 };
  const pct = ((current - prev) / prev) * 100;
  if (pct > 0) return { direction: "up", pct: pct.toFixed(1) };
  if (pct < 0) return { direction: "down", pct: Math.abs(pct).toFixed(1) };
  return { direction: "neutral", pct: 0 };
};

const STAT_TONES = {
  primary: "bg-primary/10 text-primary",
  success: "bg-success-500/15 text-success-700",
  warning: "bg-warning-500/15 text-warning-700",
  info: "bg-info-500/15 text-info-700",
} as const;

export type StatTone = keyof typeof STAT_TONES;

/** Big number + label + tinted icon, with an optional trend line. */
export const StatCard = ({ label, tooltip, value, icon, tone = "primary", trend }: {
  label: string; tooltip?: string; value: ReactNode; icon: ReactNode; tone?: StatTone;
  trend?: { direction: string; pct: number | string } | null;
}) => (
  <div className="kpi-card flex min-w-0 items-start gap-3 rounded-xl border border-border bg-card p-4 text-card-foreground shadow-sm">
    <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-lg [&_svg]:size-5", STAT_TONES[tone])} aria-hidden="true">{icon}</span>
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="kpi-card-value text-2xl font-semibold leading-8 tabular-nums">{value}</span>
      <span className="kpi-card-label flex items-center gap-1 text-sm text-muted-foreground">
        {label}
        {tooltip && (
          <span title={tooltip} aria-label={tooltip} className="inline-flex cursor-help">
            <Info className="size-3.5" aria-hidden="true" />
          </span>
        )}
      </span>
      {trend && (
        <span className={cn("kpi-card-trend mt-1 inline-flex items-center gap-0.5 text-xs", trend.direction,
          trend.direction === "up" && "text-success-700",
          trend.direction === "down" && "text-destructive",
          trend.direction === "neutral" && "text-muted-foreground")}>
          {trend.direction === "up" && <><ArrowUpRight className="size-3.5" aria-hidden="true" />{trend.pct}% vs previous period</>}
          {trend.direction === "down" && <><ArrowDownRight className="size-3.5" aria-hidden="true" />{trend.pct}% vs previous period</>}
          {trend.direction === "neutral" && <span>No previous data</span>}
        </span>
      )}
    </div>
  </div>
);

export const StatGrid = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div className={cn("kpi-cards-row grid gap-3 sm:grid-cols-2", className)}>{children}</div>
);

/** Light, compact sortable table header cell. */
export const SortTh = ({ label, active, dir, onClick }: { label: string; active: boolean; dir: string; onClick?: () => void }) => (
  <th className="px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
    aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : undefined}>
    {onClick ? (
      <button type="button" onClick={onClick} className="inline-flex cursor-pointer items-center gap-1 uppercase hover:text-foreground">
        {label}
        <span className={cn("sort-icon text-[10px]", active ? "active text-primary" : "opacity-50")} aria-hidden="true">
          {active ? (dir === "asc" ? "▲" : "▼") : "⇅"}
        </span>
      </button>
    ) : label}
  </th>
);

export const tableClass = "usage-table w-full text-sm [&_td]:px-3 [&_td]:py-2 [&_tbody_tr]:border-t [&_tbody_tr]:border-border";
